import React, { useState, useEffect } from "react";
import "./SubscriptionPopup.scss";
import ApiService from "./../../services/api-service";
import { getErrorString, getUserId, getUserDetail } from "./../../constants/function";
import notifier from "./../../components/Notifier";
import Loader from "./../../components/Loader";
import { useDispatch } from "react-redux";
import { getBalance } from "../../asyncAction/asyncBalance";

const SubscriptionPopup = ({ isOpen, onClose }) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // null, 'tokens', or 'paypal'
  const dispatch = useDispatch();
  const userId = getUserId();

  // PayPal Plan IDs - Monthly only
  const PAYPAL_PLANS = {
    monthly: {
      sandbox: 'P-96L44756EL916601UNC7YG4Q',
      production: 'P-31P388067W6088546NDBAGJY'
    }
  };

  // Determine environment based on hostname
  const isProduction = window.location.hostname === 'siki.io' || window.location.hostname === 'www.siki.io';
  const isStaging = window.location.hostname === 'staging.siki.io';

  // Use production plan for siki.io, sandbox for staging and local
  const planId = isProduction ? PAYPAL_PLANS.monthly.production : PAYPAL_PLANS.monthly.sandbox;

  // Check subscription status when the popup opens
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (isOpen && userId) {
        // First check cached user data
        const cachedUserData = getUserDetail();
        if (cachedUserData && cachedUserData.hasPremium !== undefined) {
          setIsSubscribed(cachedUserData.hasPremium);
          console.log("Using cached premium status:", cachedUserData.hasPremium);
        }
        
        // Then fetch fresh data from API
        setLoading(true);
        try {
          // Get user profile to check premium status
          const response = await ApiService.USER_PROFILE();
          console.log("USER_PROFILE response:", response);

          // Check hasPremium field for premium membership status
          if (response.result && response.result.hasPremium) {
            setIsSubscribed(true);
          } else {
            setIsSubscribed(false);
          }
          
          // Update cached data
          if (response.result) {
            localStorage.setItem("userData", JSON.stringify({ userData: response.result }));
          }
        } catch (error) {
          notifier.error(getErrorString(error));
        } finally {
          setLoading(false);
        }
      }
    };
    checkSubscriptionStatus();
  }, [isOpen, userId]);


  // Handle subscription with PayPal
  const handlePayPalSubscribe = async () => {
    try {
      setLoading(true);
      

      // Create PayPal subscription using ApiService
      const response = await ApiService.CREATE_PAYPAL_SUBSCRIPTION({
        planId: planId,
        returnUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription`
      });
      
      if (response.success && response.approvalUrl) {
        notifier.info("Redirecting to PayPal...");
        // Redirect to PayPal for approval
        window.location.href = response.approvalUrl;
      } else {
        notifier.error(response.error || "Failed to create PayPal subscription");
      }
    } catch (error) {
      notifier.error("Error creating PayPal subscription: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle subscription with Apple Pay (Stripe)
  const handleApplePaySubscribe = async () => {
    try {
      setLoading(true);

      // Create Stripe checkout session
      const response = await ApiService.CREATE_STRIPE_CHECKOUT({
        planType: 'monthly',
        successUrl: `${window.location.origin}/subscription/success`,
        cancelUrl: `${window.location.origin}/subscription`
      });

      if (response.success && response.url) {
        notifier.info("Redirecting to checkout...");
        // Redirect to Stripe checkout
        window.location.href = response.url;
      } else {
        notifier.error(response.error || "Failed to create checkout session");
      }
    } catch (error) {
      notifier.error("Error creating checkout: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Main subscribe handler
  const handleSubscribe = () => {
    if (paymentMethod === 'paypal') {
      handlePayPalSubscribe();
    } else if (paymentMethod === 'applepay') {
      handleApplePaySubscribe();
    }
  };

  return (
    isOpen && (
      <div className="popup-overlay">
        <div className="popup-box">
          <div className="popup-header">
            <h2>PREMIUM SUBSCRIPTION</h2>
            <button className="close-btn" onClick={onClose}>X</button>
          </div>
          {loading ? (
            <Loader />
          ) : (
            <>
              <ul className="popup-content">
                <li><span role="img" aria-label="robot">🤖</span> Unlimited access to Siki Bot AI</li>
                <li><span role="img" aria-label="music-note">🎵</span> 750 monthly streams</li>
                <li><span role="img" aria-label="nft">🎨</span> 30% off on NFT creations</li>
                <li><span role="img" aria-label="badge">✔️</span> Premium badge in-app</li>
              </ul>
              
              {!isSubscribed && paymentMethod === null && (
                <>
                  <div className="payment-options">
                    <h3>Choose Payment Method</h3>
                    <div className="payment-buttons">
                      <button
                        className="payment-option-btn applepay-btn"
                        onClick={() => setPaymentMethod('applepay')}
                      >
                        <span className="payment-icon"></span>
                        <span className="payment-text">
                          <strong>Pay with Apple Pay</strong>
                          <small>Also accepts cards & Google Pay</small>
                        </span>
                      </button>
                      <button
                        className="payment-option-btn paypal-btn"
                        onClick={() => setPaymentMethod('paypal')}
                      >
                        <span className="payment-icon">💳</span>
                        <span className="payment-text">
                          <strong>Pay with PayPal</strong>
                          <small>Auto-renewal • Cancel anytime</small>
                        </span>
                      </button>
                    </div>
                  </div>
                  <p className="billing-info">* $9.99/month</p>
                </>
              )}

              {!isSubscribed && (paymentMethod === 'paypal' || paymentMethod === 'applepay') && (
                <>
                  <div className="plan-confirmation">
                    <h3>Premium Monthly Subscription</h3>
                    <p className="plan-price">$9.99/month</p>
                    <p className="plan-note">Cancel anytime • Auto-renewal</p>
                  </div>
                  <div className="popup-footer">
                    <button className="back-btn" onClick={() => setPaymentMethod(null)}>
                      ← Back
                    </button>
                    <button
                      className={`subscribe-btn ${paymentMethod === 'applepay' ? 'applepay' : 'paypal'}`}
                      onClick={handleSubscribe}
                    >
                      {paymentMethod === 'applepay' ? 'Continue to Checkout' : 'Subscribe with PayPal'}
                    </button>
                  </div>
                </>
              )}


              {isSubscribed && (
                <div className="popup-footer">
                  <button className="subscribe-btn" disabled>
                    Already Subscribed
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  );
};

export default SubscriptionPopup;